import React, { useEffect, useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement } from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import { dashboardAPI } from '../services/api';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement);

interface DashboardData {
  total_assets: number;
  total_liabilities: number;
  net_worth: number;
  assets_by_category: { category: string; total: number }[];
  liabilities_by_category: { category: string; total: number }[];
  budget_by_priority: { priority: string; total: number }[];
  history: { date: string; net_worth: number }[];
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await dashboardAPI.get();
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div>Loading dashboard...</div>;
  }

  if (!data) {
    return <div>Failed to load dashboard data</div>;
  }

  const priorityColors: Record<string, string> = {
    Critical: '#ef4444',
    Important: '#f59e0b',
    Luxury: '#8b5cf6',
  };

  const budgetChartData = {
    labels: data.budget_by_priority.map(b => b.priority),
    datasets: [{
      data: data.budget_by_priority.map(b => b.total),
      backgroundColor: data.budget_by_priority.map(b => priorityColors[b.priority] || '#6366f1'),
      borderWidth: 0,
    }],
  };

  const assetColors = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899'];
  const assetsChartData = {
    labels: data.assets_by_category.map(a => a.category),
    datasets: [{
      data: data.assets_by_category.map(a => a.total),
      backgroundColor: assetColors.slice(0, data.assets_by_category.length),
      borderWidth: 0,
    }],
  };

  const historyChartData = {
    labels: data.history.map(h => new Date(h.date).toLocaleDateString('en-CA', { month: 'short', year: '2-digit' })),
    datasets: [{
      label: 'Net Worth',
      data: data.history.map(h => h.net_worth),
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      fill: true,
      tension: 0.4,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          color: '#94a3b8',
          padding: 16,
          usePointStyle: true,
        },
      },
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: { color: '#334155' },
        ticks: { color: '#94a3b8' },
      },
      y: {
        grid: { color: '#334155' },
        ticks: {
          color: '#94a3b8',
          callback: (value: number) => formatCurrency(value),
        },
      },
    },
  };

  const totalBudget = data.budget_by_priority.reduce((sum, b) => sum + b.total, 0);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Your financial overview</p>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-card-label">Total Assets</div>
          <div className="summary-card-value positive">
            {formatCurrency(data.total_assets)}
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">Total Liabilities</div>
          <div className="summary-card-value negative">
            {formatCurrency(data.total_liabilities)}
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">Net Worth</div>
          <div className={`summary-card-value ${data.net_worth >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(data.net_worth)}
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">Monthly Budget</div>
          <div className="summary-card-value">
            {formatCurrency(totalBudget)}
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid-2" style={{ marginBottom: '1.5rem' }}>
        {/* Budget by Priority */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Budget by Priority</h3>
          </div>
          <div className="priority-chart">
            <div style={{ width: '200px', height: '200px' }}>
              <Doughnut data={budgetChartData} options={{ ...chartOptions, plugins: { legend: { display: false } } }} />
            </div>
            <div className="priority-legend">
              {data.budget_by_priority.map((item) => (
                <div key={item.priority} className="legend-item">
                  <div
                    className="legend-color"
                    style={{ backgroundColor: priorityColors[item.priority] }}
                  />
                  <span className="legend-label">{item.priority}</span>
                  <span className="legend-value">{formatCurrency(item.total)}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            {data.budget_by_priority.map((item) => {
              const percentage = totalBudget > 0 ? (item.total / totalBudget * 100).toFixed(0) : 0;
              return (
                <span key={item.priority} className={`badge badge-${item.priority.toLowerCase()}`}>
                  {item.priority}: {percentage}%
                </span>
              );
            })}
          </div>
        </div>

        {/* Assets by Category */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Assets by Category</h3>
          </div>
          <div className="chart-container">
            <Doughnut data={assetsChartData} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* Net Worth Trend */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Net Worth Trend</h3>
        </div>
        <div style={{ height: '300px' }}>
          {data.history.length > 0 ? (
            <Line data={historyChartData} options={lineChartOptions as any} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-secondary)' }}>
              No history data yet. Create snapshots to track your progress over time.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
